/**
 * Click a rough centreline for crest extraction — the second way of
 * supplying a longitudinal guide (alongside picking an existing alignment
 * in `CrestLineDialog`), for embankments (dams, dykes, spoil heaps) that
 * have no alignment in the design file.
 *
 * Raycasting: uses the shared `pickSurface` helper against both point-cloud
 * octrees and standard meshes, driven off DOM-level `click` events on the
 * canvas — exactly like `AddLineTool` / `MeasuringTool` — rather than R3F's
 * built-in `onClick`. Potree octrees are picked with `pco.pick()` and are
 * never traversed by R3F's raycaster, so a plain `<group onClick>` would
 * silently miss every point-cloud surface, which is the common case for the
 * embankments this tool exists for.
 *
 * SCREEN-SPACE SIZING: vertex marker scale comes from the shared
 * `getUnitsPerPixel` helper (`./screenScale`), never from
 * `camera.position.distanceTo()` — see CLAUDE.md "Screen-space sized
 * handles & markers": the ortho camera is dollied to 1000 m (vs 50 m
 * perspective) so camera distance reads ~10x too big there. The scale is
 * applied to each marker's GROUP inside useFrame — never baked into JSX
 * geometry args, which would freeze the size at render time and stop
 * tracking zoom.
 *
 * `crestGuideDraftAtom` only stores XY (guideFromXY drops Z — only the
 * station axis and section normal matter, see its docstring), so a parallel
 * XYZ mirror (`crestGuideDraft3DAtom`) exists purely so the sketch renders at
 * the picked surface height instead of z=0. Both mirrors are mutated together
 * by the shared action atoms (add / undo / finish / cancel) in
 * `crestLine.atom.ts`; this component never writes either one directly.
 *
 * Those actions are shared on purpose: `CrestGuideToolbar` renders plain DOM
 * as a sibling of the `<Canvas>` and drives the very same atoms, so its Undo /
 * Finish / Cancel buttons and this file's Backspace / Enter / Escape are one
 * code path, not two implementations that can drift.
 */
import {
  crestGuideAddVertexAtom,
  crestGuideCancelAtom,
  crestGuideClearDraftAtom,
  crestGuideDraft3DAtom,
  crestGuideFinishAtom,
  crestGuideModeAtom,
  crestGuideUndoVertexAtom,
} from '@/atoms/crestLine.atom';
import { meshPickTargetsAtom, pointCloudTargetsAtom } from '@/atoms/viewer.atom';
import { Line } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { attachLeftClickPick } from '@/lib/leftClickPick';
import { pickSurface } from './pickSurface';
import { getUnitsPerPixel } from './screenScale';

const GUIDE_COLOR = '#ffc400';
const MARKER_PIXELS = 5;

const isTypingTarget = (e: KeyboardEvent) => {
  const t = e.target as HTMLElement | null;
  if (!t) return false;
  return t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable;
};

/**
 * Screen-space-sized vertex marker. Scale is derived every frame from
 * `getUnitsPerPixel` and applied to this group — never to the sphere's
 * geometry args, which are captured once at render time.
 */
function GuideVertexMarker({ position }: { position: THREE.Vector3 }) {
  const groupRef = useRef<THREE.Group>(null);
  const camera = useThree((s) => s.camera);
  const renderer = useThree((s) => s.gl);
  const worldRef = useRef(new THREE.Vector3());

  useFrame(() => {
    const g = groupRef.current;
    if (!g) return;
    // World position, not `position`: this marker sits inside the
    // floating-origin parent group below, so its local position is tiny
    // and meaningless for the perspective branch of getUnitsPerPixel.
    g.getWorldPosition(worldRef.current);
    const unitsPerPixel = getUnitsPerPixel(camera, renderer, worldRef.current);
    g.scale.setScalar(MARKER_PIXELS * unitsPerPixel);
  });

  return (
    <group ref={groupRef} position={position}>
      <mesh>
        <sphereGeometry args={[1, 12, 12]} />
        <meshBasicMaterial color={GUIDE_COLOR} depthTest={false} />
      </mesh>
    </group>
  );
}

export function CrestGuideTool() {
  const mode = useAtomValue(crestGuideModeAtom);
  const draft3D = useAtomValue(crestGuideDraft3DAtom);
  const addVertex = useSetAtom(crestGuideAddVertexAtom);
  const undoVertex = useSetAtom(crestGuideUndoVertexAtom);
  const finishSketch = useSetAtom(crestGuideFinishAtom);
  const cancelSketch = useSetAtom(crestGuideCancelAtom);
  const clearDraft = useSetAtom(crestGuideClearDraftAtom);
  const camera = useThree((s) => s.camera);
  const renderer = useThree((s) => s.gl);
  const raycaster = useMemo(() => new THREE.Raycaster(), []);
  const pcoTargets = useAtomValue(pointCloudTargetsAtom);
  const meshTargets = useAtomValue(meshPickTargetsAtom);

  // Vector3s for rendering, rebuilt only when the XYZ mirror changes.
  const points = useMemo(() => draft3D.map(([x, y, z]) => new THREE.Vector3(x, y, z)), [draft3D]);

  // Latest pick targets via refs so the click listener doesn't have to
  // rebind (and risk dropping a click) every time layer visibility changes.
  const pcoTargetsRef = useRef(pcoTargets);
  pcoTargetsRef.current = pcoTargets;
  const meshTargetsRef = useRef(meshTargets);
  meshTargetsRef.current = meshTargets;

  // Start every drawing session from a blank sketch — a stale draft from a
  // previous session (e.g. after Enter committed it into a guide) must not
  // bleed into the next one.
  useEffect(() => {
    if (mode) clearDraft();
  }, [mode, clearDraft]);

  // Click-to-pick via attachLeftClickPick (orbit drags and the second click
  // of a double-click don't drop vertices), mirroring AddLineTool rather
  // than R3F's onClick. No IFC-fragment-pick
  // fallback (unlike AddLineTool/MeasuringTool): the embankments this tool
  // targets (dams, dykes, spoil heaps) are LandXML/point-cloud surfaces, not
  // IFC models — pickSurface already covers both.
  useEffect(() => {
    if (!mode) return;
    const dom = renderer.domElement;

    const onClick = (e: PointerEvent) => {
      const rect = dom.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
      const hit = pickSurface(renderer, camera, raycaster, pcoTargetsRef.current, meshTargetsRef.current);
      if (!hit) return;
      const p = hit.position;
      addVertex([p.x, p.y, p.z]);
    };

    return attachLeftClickPick(dom, onClick);
  }, [mode, camera, renderer, raycaster, addVertex]);

  // Keyboard: Escape clears the sketch AND leaves draw mode. Enter leaves
  // draw mode WITH the draft intact — CrestLineDialog watches that
  // mode:false + non-empty-draft transition to convert the sketch into a
  // Guide via guideFromXY. Backspace / Ctrl|Cmd+Z undoes the last vertex.
  //
  // CAPTURE phase on `window`, and Escape additionally stops propagation.
  // The dialog that owns this tool is a Radix dialog, and Radix's
  // `useEscapeKeydown` listens on `document` in the capture phase and
  // dismisses the layer. Propagation order is window-capture →
  // document-capture, so this is the only phase from which "Esc cancels the
  // SKETCH" can win over "Esc closes the DIALOG" — which is what the drawing
  // hint promises, and what leaves the user somewhere useful. Nothing else
  // is stopped: Enter, Backspace and Ctrl/Cmd+Z propagate as before, and a
  // keystroke aimed at a text field short-circuits above.
  useEffect(() => {
    if (!mode) return;
    const onKey = (e: KeyboardEvent) => {
      if (isTypingTarget(e)) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        cancelSketch();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        finishSketch();
      } else if (e.key === 'Backspace' || (e.key === 'z' && (e.ctrlKey || e.metaKey))) {
        e.preventDefault();
        undoVertex();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [mode, cancelSketch, finishSketch, undoVertex]);

  if (!mode) return null;

  // Floating origin: render everything relative to the first picked vertex
  // so we don't push UTM-scale coordinates through the GPU's float32
  // pipeline (same reasoning as AddLineTool / AlignmentOverlay).
  const origin = points[0] ?? new THREE.Vector3(0, 0, 0);
  const relPoints = points.map((p) => p.clone().sub(origin));

  return (
    <group position={origin}>
      {relPoints.length >= 2 && (
        <Line points={relPoints} color={GUIDE_COLOR} lineWidth={2} transparent opacity={0.9} depthTest={false} />
      )}
      {relPoints.map((p, i) => (
        <GuideVertexMarker key={i} position={p} />
      ))}
    </group>
  );
}
